import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost, ApiError } from "../api";
import type { SeasonPhase } from "./memberships";
import type { ID } from "../id";

export interface SeasonAdmin {
  id: ID;
  code: string;
  /** The rule set that answers questions about this season. See Season. */
  default_rule_set_id?: ID | null;
  default_rule_set_name?: string;
  registration_open_from?: string | null;
  registration_open_to?: string | null;
  registration_open: boolean;
  phase: SeasonPhase;
  active: boolean;
  config?: string;
}

export interface SeasonWritePayload {
  code?: string;
  /** 0 clears the default; omitting the field leaves it alone. */
  default_rule_set_id?: ID;
  registration_open_from?: string | null;
  registration_open_to?: string | null;
  active?: boolean;
  config?: string;
}

export function useAdminSeasons() {
  return useQuery<SeasonAdmin[]>({
    queryKey: ["admin", "seasons"],
    queryFn: async () => {
      const res = await apiGet<{ data: SeasonAdmin[] }>("/seasons");
      return res.data;
    },
  });
}

export function useCreateSeason() {
  const qc = useQueryClient();
  return useMutation<SeasonAdmin, ApiError, SeasonWritePayload>({
    mutationFn: (payload) => apiPost<SeasonAdmin>("/seasons", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "seasons"] });
      qc.invalidateQueries({ queryKey: ["seasons"] });
    },
  });
}

export function useUpdateSeason(id: ID) {
  const qc = useQueryClient();
  return useMutation<SeasonAdmin, ApiError, SeasonWritePayload>({
    mutationFn: (payload) => apiPatch<SeasonAdmin>(`/seasons/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "seasons"] });
      qc.invalidateQueries({ queryKey: ["seasons"] });
    },
  });
}

export function useActivateSeason() {
  const qc = useQueryClient();
  return useMutation<SeasonAdmin, ApiError, ID>({
    mutationFn: (id) => apiPost<SeasonAdmin>(`/seasons/${id}/activate`, {}),
    // Reported inside the confirm dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "seasons"] });
      qc.invalidateQueries({ queryKey: ["seasons"] });
    },
  });
}
